<template>
  <GoalCardShell
    @click="handleCardClick"
    :restriction-type="restrictionType"
    :is-anchor="isAnchor"
    :route="route"
  >
    <GoalCardText
      :title="displayedTitle"
      :text="displayedText"
      :blur-lock="blurLock" />

    <template v-slot:middle>
      <GoalCardLevelIndicators
        :grades="goal.grades"
        :level="goal.level"
        :level-label="levelLabel"
        :levels="goal.levels"
        :anchor-styling="isAnchor"
        :blur-lock="blurLock"
      />
    </template>

    <template v-slot:footer>
      <GoalCardFooter
        :standard-set-label="(shouldDisplayStandardSetLabel && standardSetLabel) || ''"
        :standard-ref="shortStandardCodeForGoal"
        :show-progress="goal.has_progress_monitoring"
        :resource-languages="goal.resource_languages"
        :show-grow="isGrowGoal"
        :blur-lock="blurLock"
      />
    </template>
  </GoalCardShell>
</template>

<script>
import GoalCardShell from './GoalCardShell'
import GoalCardText from './GoalCardText'
import GoalCardLevelIndicators from './GoalCardLevelIndicators'
import GoalCardFooter from './GoalCardFooter'

import {
  RestrictionTypePremium,
  restrictionValidator,
} from '../browse_goal_card_restrictions'
import filters from '@/filters'

import { mapGetters, mapState } from 'vuex'

import { AccessPointsUtil } from '@/components/goals/common/access_points'
import { isSubjectELL } from '@/util/goals'
import { AdaptedGoalValues } from '@/util/adapted_goal_levels'

import { sendAlgoliaClickedBrowseGoalResultEvent } from '@/api/algolia'
import { getPlaceholderText } from '@/util/placeholder_text'
import { hasGrowGoal } from '@/util/growGoalChecker'

const ellLevelLabels = {
  [AdaptedGoalValues.GRADE]: 'Advanced',
  [AdaptedGoalValues.INTERMEDIATE]: 'Intermediate',
  [AdaptedGoalValues.BEGINNER]: 'Beginner',
}

const legacyLevelLabels = {
  [AdaptedGoalValues.GRADE]: 'Grade Level',
  [AdaptedGoalValues.MILD]: 'Mild',
  [AdaptedGoalValues.MODERATE]: 'Moderate',
  [AdaptedGoalValues.INTENSE]: 'Intense',
}

export default {
  name: 'GoalCardContainer',
  components: {
    GoalCardShell,
    GoalCardText,
    GoalCardLevelIndicators,
    GoalCardFooter,
  },
  props: {
    goal: {
      type: Object,
      required: true,
    },
    restrictionType: {
      type: String,
      default: '',
      validator: restrictionValidator,
    },
    standardSetLabel: {
      type: String,
      required: true,
    },
    standardSetForRefDisplay: {
      type: String,
      required: true,
    },
    position: {
      type: Number,
      required: false,
      default: -1,
    },
    alignedGoalsTile: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      longRefToStandardMappings: 'browse/longRefToStandardMappings',
      browseFilters: 'browse/filters',
    }),
    ...mapState('browse', ['algoliaQueryID']),
    ...mapGetters('user', ['userId']),

    isAnchor() {
      return this.goal.super_page || false
    },
    route() {
      if (this.isAnchor) {
        return {
          name: 'AnchorPage',
          params: {
            uuid: this.goal.uuid,
          },
          query: {
            browse_subject: this.browseFilters.subject,
            browse_domain: this.browseFilters.subjectDomain,
          },
        }
      } else {
        const query = {
          browse_subject: this.browseFilters.subject,
          browse_domain: this.browseFilters.subjectDomain,
        }
        if (this.goal.level) {
          query.expanded_level = this.goal.level
        }
        return {
          name: 'GoalContentPage',
          params: {
            slug: this.goal.slug,
          },
          query,
        }
      }
    },
    shouldDisplayStandardSetLabel() {
      return this.goal.standard_set_refs && this.goal.standard_set_refs.some(
        (setRef) => setRef.standard_set_code === this.standardSetLabel
      )
    },
    shortStandardCodeForGoal() {
      if (this.blurLock) {
        return 'AAA.111'
      }
      if (
        !this.longRefToStandardMappings ||
        Object.keys(this.longRefToStandardMappings).length === 0 ||
        !this.standardSetForRefDisplay ||
        !this.goal.standard_set_refs ||
        this.goal.standard_set_refs.length === 0
      ) {
        return ''
      }

      let shortCode = ''
      let stdRefs = filters.standardRefsForSetCode(
        this.standardSetForRefDisplay,
        this.goal.standard_set_refs
      )
      if (stdRefs.length > 0) {
        shortCode =
          (this.longRefToStandardMappings[stdRefs[0]] &&
            this.longRefToStandardMappings[stdRefs[0]].standard_short_ref) ||
          ''
      }
      return shortCode
    },
    levelLabel() {
      if (this.isAnchor) {
        return AccessPointsUtil.labelsMap[this.goal.level]
      }
      if (isSubjectELL(this.goal.subject)) {
        return ellLevelLabels[this.goal.level]
      }
      return legacyLevelLabels[this.goal.level] || this.goal.level
    },
    blurLock() {
      return this.restrictionType === RestrictionTypePremium
    },
    displayedTitle() {
      if (this.blurLock) {
        return getPlaceholderText(40)
      }
      return this.goal.title
    },
    displayedText() {
      if (this.blurLock) {
        return getPlaceholderText(100)
      }
      return this.goal.goal_text
    },
    isGrowGoal() {
      return hasGrowGoal(this.goal.uuid)
    }
  },
  methods: {
    handleCardClick() {
      if (this.alignedGoalsTile) {
        this.$sendEvent(
          'Clicked Aligned Goal Tile',
          {},
          {
            cu: this.goal.uuid,
            ct: this.goal.title,
          }
        )
      } else {
        this.$sendEvent(
          'Clicked Browse Goal Tile',
          {},
          {
            cu: this.goal.uuid,
            ct: this.goal.title,
          }
        )
      }

      if (this.algoliaQueryID && this.goal.objectID) {
        sendAlgoliaClickedBrowseGoalResultEvent({
          userToken: this.userId,
          queryID: this.algoliaQueryID,
          objectIDs: [this.goal.objectID],
          positions: [this.position + 1], // algolia requires positions be strictly positive (not 0-based)
        })
      }
    },
  },
  eventContext() {
    return {
      algolia_query_id: this.algoliaQueryID || undefined,
      browse_goals_tile_goal_uuid: this.goal.uuid,
      browse_goals_tile_goal_title: this.goal.title,
      browse_goals_tile_is_anchor: this.isAnchor,
      browse_goals_tile_position: this.position,
      browse_goals_tile_is_udl_anchor: false, // This will be a field in the future
      browse_goals_tile_is_premium: this.restrictionType === RestrictionTypePremium,
      browse_goals_tile_is_invite_10: false,
      browse_goals_tile_is_unlocked: false,
    }
  },
}
</script>
